import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { AppConfigService } from '../../core/config/app-config.service';
import { AuthServerProvider } from '../../core/auth/auth-server-provider.service';
import { CandidateSessionService } from '../../core/auth/candidate-session.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

function addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}

export const authExpiredInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const appConfig = inject(AppConfigService);
  const authServerProvider = inject(AuthServerProvider);
  const candidateSession = inject(CandidateSessionService);

  // MSAL mode: short-circuit — MsalInterceptor handles tokens via DI.
  // KCS-565 Part 2: a candidate session on MSL is the exception — candidates carry our own JWT (not an
  // Azure token), so they take the LDAP-style Bearer-attach + stateless-refresh path below.
  if (appConfig.microsoftLogin && !candidateSession.isActive()) {
    return next(req);
  }

  // LDAP / candidate mode: attach Bearer token
  const token = authServerProvider.getAccessToken();
  const isAuthEndpoint = req.url.includes('/authentication') || req.url.includes('/auth/refresh');
  const isExternalApi = req.url.includes('graph.microsoft.com');
  let request = req;

  if (token && !isAuthEndpoint && !isExternalApi) {
    request = addToken(req, token);
  }

  return next(request).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && error.status === 401 && !isExternalApi && !isAuthEndpoint) {
        return handle401Error(request, next, router, authServerProvider);
      }
      return throwError(() => error);
    })
  );
};

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  router: Router,
  authServerProvider: AuthServerProvider
): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = authServerProvider.getRefreshToken();

    if (refreshToken) {
      return authServerProvider.refreshToken(refreshToken).pipe(
        switchMap((response: any) => {
          isRefreshing = false;
          authServerProvider.setTokens(response.token, response.refreshToken);
          refreshTokenSubject.next(response.token);
          return next(addToken(request, response.token));
        }),
        catchError((err) => {
          isRefreshing = false;
          authServerProvider.clearTokens();
          router.navigate(['/login']);
          return throwError(() => err);
        })
      );
    } else {
      isRefreshing = false;
      authServerProvider.clearTokens();
      router.navigate(['/login']);
      return throwError(() => new Error('Refresh token missing'));
    }
  } else {
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next(addToken(request, token!)))
    );
  }
}
